// ============================================================================
// BESPOKIAN Health - Footer Component
// Luxury pharmacy footer with trust badges, links, and contact info
// ============================================================================

import { Link, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin, Shield, Award, Clock } from 'lucide-react';

const footerLinks = {
  medications: [
    { label: 'Heart & Blood Pressure', href: '/medications/heart' },
    { label: 'Diabetes & Metabolic', href: '/medications/diabetes' },
    { label: 'Mental Health', href: '/medications/mental-health' },
    { label: 'All Medications', href: '/medications' },
  ],
  company: [
    { label: 'About Us', href: '/about' },
    { label: 'How It Works', href: '/how-it-works' },
    { label: 'For Employers', href: '/employers' },
    { label: 'Careers', href: '/careers' },
  ],
  support: [
    { label: 'FAQ', href: '/faq' },
    { label: 'Contact', href: '/contact' },
    { label: 'Shipping Info', href: '/shipping' },
  ],
  legal: [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Terms of Service', href: '/terms' },
    { label: 'HIPAA Notice', href: '/hipaa' },
    { label: 'HIPAA Tracking', href: '/hipaa-tracking' },
  ],
};

const socialLinks = [
  { icon: Facebook, href: 'https://facebook.com/bespokianhealth', label: 'Facebook' },
  { icon: Twitter, href: 'https://twitter.com/bespokianhealth', label: 'Twitter' },
  { icon: Instagram, href: 'https://instagram.com/bespokianhealth', label: 'Instagram' },
  { icon: Linkedin, href: 'https://linkedin.com/company/bespokian', label: 'LinkedIn' },
];

export function Footer() {
  const currentYear = new Date().getFullYear();
  const router = useRouterState();

  // Scroll to top on route change
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [router.location.pathname]);

  // Scroll to top handler for link clicks
  const handleLinkClick = () => {
    // Immediate scroll, then again after navigation
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Also scroll after a brief delay to catch route changes
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 150);
  };

  return (
    <footer className="bg-navy-800 text-navy-200" role="contentinfo">
      {/* Trust Badges */}
      <div className="border-b border-navy-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gold-500/20 rounded-xl flex items-center justify-center">
                <Shield className="h-6 w-6 text-gold-400" />
              </div>
              <div>
                <h4 className="font-semibold text-white">Licensing In Progress</h4>
                <p className="text-sm text-navy-300">Currently in licensing phase</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-sage-500/20 rounded-xl flex items-center justify-center">
                <Award className="h-6 w-6 text-sage-400" />
              </div>
              <div>
                <h4 className="font-semibold text-white">HIPAA In Progress</h4>
                <p className="text-sm text-navy-300">Compliance in progress</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gold-500/20 rounded-xl flex items-center justify-center">
                <Clock className="h-6 w-6 text-gold-400" />
              </div>
              <div>
                <h4 className="font-semibold text-white">Pharmacist Support (soon)</h4>
                <p className="text-sm text-navy-300">Licensed pharmacists soon</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" onClick={handleLinkClick} className="inline-flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center">
                <img src="/logo.svg" alt="BESPOKIAN" className="h-10 w-10 object-contain" />
              </div>
              <div>
                <span className="font-display text-xl text-white tracking-tight">BESPOKIAN</span>
                <span className="block text-gold-500 text-[10px] tracking-[0.3em] uppercase -mt-1 font-medium">
                  HEALTH
                </span>
              </div>
            </Link>

            <p className="text-navy-300 mb-6 max-w-sm">
              Transparent prescription pricing. Drug cost + 15% + $5 fee. No PBM middlemen. Potential savings up to 80%.
            </p>

            <div className="space-y-3">
              <a
                href="tel:1-877-237-7679"
                className="flex items-center gap-3 text-navy-200 hover:text-gold-400 transition-colors"
              >
                <Phone className="h-4 w-4" />
                <span>1-877-BES-PORX</span>
              </a>
              <a
                href="mailto:hello@bespokianhealth.com"
                className="flex items-center gap-3 text-navy-200 hover:text-gold-400 transition-colors"
              >
                <Mail className="h-4 w-4" />
                <span>hello@bespokianhealth.com</span>
              </a>
              <div className="flex items-center gap-3 text-navy-300">
                <MapPin className="h-4 w-4" />
                <span>Altamonte Springs, FL</span>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-navy-700 rounded-lg flex items-center justify-center text-navy-300 hover:bg-gold-500 hover:text-navy-800 transition-all"
                  aria-label={social.label}
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Link Columns */}
          <div>
            <h3 className="font-semibold text-gold-400 text-sm uppercase tracking-wider mb-4">Medications</h3>
            <ul className="space-y-3">
              {footerLinks.medications.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} onClick={handleLinkClick} className="text-navy-300 hover:text-white transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-gold-400 text-sm uppercase tracking-wider mb-4">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} onClick={handleLinkClick} className="text-navy-300 hover:text-white transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-gold-400 text-sm uppercase tracking-wider mb-4">Support</h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} onClick={handleLinkClick} className="text-navy-300 hover:text-white transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-gold-400 text-sm uppercase tracking-wider mb-4">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} onClick={handleLinkClick} className="text-navy-300 hover:text-white transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-navy-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-navy-400">© {currentYear} BESPOKIAN Health. All rights reserved.</p>
            <p className="text-xs text-navy-400 text-center md:text-right">
              Not affiliated with any PBM. Transparent pricing, always.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}



